/**
 * Builder EmailSignup custom component.
 *
 * Delegates to the shared `InputWithCta` and posts to the
 * `/resources/email-signup` resource route for server-side validation.
 */
import { InputWithCta } from "hec/components/ui/input-with-cta";
import { useCallback } from "react";

import type { RegisteredComponent } from "@builder.io/sdk-react";

import { useSite } from "~/contexts";
import {
  emitFormInitiate,
  emitFormSubmit,
} from "~/hec/analytics/event-layer/gtm-form-events";
import { cn } from "~/lib/utils";
import { useRootLoaderData } from "~/root";
import { fireEmailSignup } from "~/segment/consent";
import { useSetTpaConsent } from "~/segment/use-tpa-consent";

interface BuilderEmailSignupProps {
  whiteMode?: boolean;
  placement?: string;
  attributes?: {
    className?: string;
    [key: string]: unknown;
  };
  "data-testid"?: string;
}

const BuilderEmailSignup: React.FC<BuilderEmailSignupProps> = ({
  whiteMode,
  placement,
  attributes,
  "data-testid": dataTestId,
}) => {
  const { className } = attributes || {};
  const { site } = useSite();
  // TPA consent capture (SFCC-11425): for a registered TPA shopper whose profile
  // consent flag is still missing (`segmentTpaSuppressed` legacy root-loader
  // name), submitting the signup IS the opt-in — mirror MRT's EmailSignup →
  // setExplicitConsent and write the profile flag.
  const { segmentTpaSuppressed, isRegistered } = useRootLoaderData();
  const captureTpaConsent = useSetTpaConsent();

  // `placement` is a required Builder input; default to 'footer' (SFRA
  // Segment-SubscribeEmailPhone default) so the posted form field and the
  // analytics event always agree on the same value.
  const resolvedPlacement = placement ?? "footer";

  // SFCC-11017 (ANALYTICS-25/26/28): on a client-valid signup fire
  // popupEmailSignup (GTM) + Segment identify + email consent. Submitting the
  // banner IS the opt-in (see disclaimer), so action='accept'.
  const handleValidSubmit = useCallback(
    (values: Record<string, string>) => {
      const email = values.emailSignup;
      if (!email) return;
      fireEmailSignup({ email, siteId: site.id, placement: resolvedPlacement });
      // Registered TPA shopper opting in via email signup → persist consent so
      // the root-loader TPA consent flag flips.
      // Guests are skipped (no profile to PATCH; deferred to the guest-merge
      // follow-up) — the server action also enforces this.
      if (segmentTpaSuppressed && isRegistered) captureTpaConsent();
    },
    [
      site.id,
      resolvedPlacement,
      segmentTpaSuppressed,
      isRegistered,
      captureTpaConsent,
    ],
  );

  return (
    <InputWithCta
      whiteMode={whiteMode}
      inputType="email"
      inputName="emailSignup"
      label="enter email address"
      ctaText="Sign Up"
      isRequired={true}
      submitMsg="Email Submitted"
      errorMsg="Please enter a valid email address"
      action="/resources/email-signup"
      placement={resolvedPlacement}
      onValidSubmit={handleValidSubmit}
      // GTM form tracking (SFCC-11889 C5): formInitiate on first focus,
      // formSubmit on every attempt; `placement` disambiguates footer/popup.
      onFirstFocus={() =>
        emitFormInitiate("emailSignup", { placement: resolvedPlacement })
      }
      onSubmitAttempt={() =>
        emitFormSubmit("emailSignup", { placement: resolvedPlacement })
      }
      className={cn(className)}
      data-testid={dataTestId || "emailSignup-InputWithCta"}
    />
  );
};

export const EmailSignupDefinition = {
  component: BuilderEmailSignup,
  name: "EmailSignup",
  noWrap: true,
  inputs: [
    {
      name: "whiteMode",
      type: "boolean",
      helperText: "Applies white mode",
      defaultValue: false,
    },
    {
      name: "placement",
      type: "string",
      required: true,
      helperText:
        "Placement value of this component that will be used for tracking purposes.",
    },
  ],
} satisfies RegisteredComponent;
